import { createRouter, createWebHistory } from "vue-router";

import LoginPage from "@/view/LoginPage.vue";

// ✅ 匯入共用 Layout
import AdminLayout from "@/components/AdminLayout.vue";

// ✅ 匯入各頁面
import SettingDashboard from "@/view/SettingDashboard.vue";
import ProductSetting from "@/view/ProductSetting.vue";
import OrderSetting from "@/view/OrderSetting.vue";
import AccountSetting from "@/view/AccountSetting.vue";
import CustomerSetting from "@/view/CustomerSetting.vue";
import BannerSetting from "@/view/BannerSetting.vue";
import ReturnSetting from "@/view/ReturnSetting.vue";
import SaleSetting from "@/view/SaleSetting.vue";
import CategorySetting from "@/view/CategorySetting.vue";
import MarketSetting from "@/view/MarketSetting.vue";
import ResetPasswordPage from "@/view/ResetPasswordPage.vue";
import PermissionSetting from "@/view/PermissionSetting.vue";

const routes = [
  {
    path: "/login",
    component: LoginPage,
  },

  // ✅ 密碼重設頁面
  {
    path: "/reset-password", // 密碼重設頁面路由
    name: "reset-password",
    component: ResetPasswordPage, // 導向重設密碼頁面
  },

  // ✅ 巢狀結構
  {
    path: "/admin", // 後台主路徑
    component: AdminLayout, // 共用的版型
    redirect: "/admin/dashboard", // 訪問 /admin 時重定向到 /admin/dashboard
    children: [
      // 內頁繼承 Layout
      {
        path: "dashboard",
        name: "dashboard",
        component: SettingDashboard,
        meta: { requiredPermission: "Dashboard" },
      },
      {
        path: "products",
        name: "products",
        component: ProductSetting,
        meta: { requiredPermission: "ProductManage" },
      },
      {
        path: "order",
        name: "order",
        component: OrderSetting,
        meta: { requiredPermission: "OrderManage" },
      },
      {
        path: "account",
        name: "account",
        component: AccountSetting,
        meta: { requiredPermission: "AccountManage" },
      },
      {
        path: "customer",
        name: "customer",
        component: CustomerSetting,
        meta: { requiredPermission: "MemberManage" },
      },
      {
        path: "banner",
        name: "banner",
        component: BannerSetting,
        meta: { requiredPermission: "BannerManage" },
      },
      {
        path: "return",
        name: "return",
        component: ReturnSetting,
        meta: { requiredPermission: "ReturnManage" },
      },
      {
        path: "category",
        name: "category",
        component: CategorySetting,
        meta: { requiredPermission: "CategoryManage" },
      },
      {
        path: "market",
        name: "market",
        component: MarketSetting,
        meta: { requiredPermission: "MarketingManage" },
      },
      {
        path: "sale",
        name: "sale",
        component: SaleSetting,
        meta: { requiredPermission: "InventoryManage" },
      },
      {
        path: "permissions",
        name: "permissions",
        component: PermissionSetting,
        meta: { requiredPermission: "PermissionManage" },
      },
    ],
  },

  // ✅ 預設轉向
  {
    path: "/",
    redirect: "/login",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 🔒 路由守卫：检查用户登入和权限状态
let cachedUserPermissions = null;

router.beforeEach(async (to, from, next) => {
  // 公开页面，直接放行
  if (
    to.path === "/login" ||
    to.path === "/reset-password" ||
    to.path === "/"
  ) {
    next();
    return;
  }

  try {
    // 所有后台页面都需要检查登入状态
    const api = (await import("@/assets/js/utils/api")).default;

    // 调用 verify API 检查是否已登入
    const res = await api.get("/back/admin/verify");

    if (res.data?.success === true && res.data?.data) {
      console.log("✅ 已登入，用戶資訊:", res.data.data);

      // 如果要求權限檢查，進行檢查
      if (to.meta?.requiredPermission) {
        try {
          // 獲取用戶的所有權限
          if (!cachedUserPermissions) {
            const permRes = await api.get("/employee/permission");
            if (permRes.data?.data) {
              cachedUserPermissions = permRes.data.data;
              console.log("✅ 成功取得用戶權限:", cachedUserPermissions);
            } else {
              console.warn("⚠️ 權限 API 返回空數據");
              next(false);
              return;
            }
          }

          // 檢查用戶是否有該頁面的權限
          const requiredPerm = to.meta.requiredPermission;
          const hasPermission = cachedUserPermissions[requiredPerm];

          console.log(
            `🔍 檢查權限: ${requiredPerm}, 用戶权限值: ${hasPermission}`,
            "完整權限:",
            cachedUserPermissions,
          );

          if (hasPermission === true) {
            // ✅ 有權限，允許訪問
            console.log(`✅ 允許訪問: ${to.path}`);
            next();
          } else {
            // ❌ 沒有權限，停留在原位並提示
            console.warn(
              `❌ 無權存取: ${to.path}，需要權限: ${requiredPerm}，用戶权限: ${hasPermission}`,
            );

            // 動態導入 alert 工具
            const { showCustom } = await import(
              "@/assets/js/utils/alertService"
            );
            showCustom({
              icon: "warning",
              title: "存取被拒",
              text: `您沒有權限訪問此頁面 (需要: ${requiredPerm})`,
            });

            // 回到上一頁，若無上一頁則導向 dashboard
            if (from.name) {
              next(from.fullPath);
            } else {
              next("/admin/dashboard");
            }
          }
        } catch (permError) {
          console.error("❌ 權限檢查出錯:", permError);
          // 權限檢查失敗，阻止訪問
          next(false);
        }
      } else {
        // 沒有權限要求，直接放行
        console.log(`✅ 路徑無權限要求，允許訪問: ${to.path}`);
        next();
      }
    } else {
      // verify 失敗，表示未登入或 token 無效
      console.warn("❌ 登入驗證失敗，重導到登入頁");
      localStorage.removeItem("token");
      next("/login");
    }
  } catch (error) {
    // API 建立或執行失敗，表示未登入
    console.warn("❌ 檢查登入狀態失敗:", error.message);

    // 清除可能過期的 token
    localStorage.removeItem("token");

    // 重導到登入頁
    if (to.path !== "/login" && to.path !== "/reset-password") {
      next("/login");
    } else {
      next();
    }
  }
});

export default router;
